<template>
  <div class="md:w-1/2 sm:w-full mx-auto px-4 py-10 text-gray-800">
    <header class="text-center">
      <h1 class="text-3xl sm:text-4xl font-extrabold tracking-tight">Privacy Policy for MyGraduationClip</h1>
      <p class="mt-3 text-base sm:text-lg">
        Last updated: <span class="font-medium">{{ lastUpdated || defaultUpdated }}</span>
      </p>
      <p class="mt-2 text-gray-600">
        At MyGraduationClip, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, who we share it with, and your rights under applicable data protection laws, including the UK GDPR and the EU GDPR. We will update this Privacy Policy on this page, showing the month and year of the latest revision.
      </p>
    </header>

    <!-- 1. Who We Are -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">1. Who We Are</h2>
      <p class="mt-3">MyGraduationClip ("we", "our", "us") provides a digital service that creates personalised graduation video clips for university students.</p>
      <div class="mt-4 space-y-3">
        <p><span class="font-semibold">Data Controller:</span> We act as a data controller when you give us data directly — for example, registration details, survey responses (both required and optional), and account preferences. In these cases, we determine what data is needed to provide the GraduationClip service.</p>
        <p><span class="font-semibold">Data Processor:</span> When your university provides us with your personal data (e.g. student lists, degree details, graduation footage, or where surveys are mandated by the university), we act as a data processor on behalf of the university and process data only under their instructions.</p>
      </div>
      <div class="mt-4 bg-gray-50 rounded-2xl p-4">
        <p>📧 Contact us: <a v-if="contactEmail" :href="`mailto:${contactEmail}`" class="text-blue-700 underline">{{ contactEmail }}</a></p>
        <p v-if="postalAddress" class="mt-1">📍 <span>{{ postalAddress }}</span><span></span></p>
        <p class="mt-3 text-sm text-gray-600">In the UK, you can also contact the ICO (Information Commissioner’s Office). In the EU, you may contact your local supervisory authority.</p>
      </div>
    </section>

    <!-- 2. Information We Collect -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">2. Information We Collect</h2>
      <div class="mt-4">
        <h3 class="font-semibold">From Your University (processor role):</h3>
        <ul class="list-disc list-inside mt-2 space-y-1">
          <li>Student name, email address, degree title, graduation date/time, student ID, and graduation footage (as defined in our Data Processing Agreement with universities).</li>
        </ul>
      </div>
      <div class="mt-6">
        <h3 class="font-semibold">Directly From You (controller role):</h3>
        <ul class="list-disc list-inside mt-2 space-y-1">
          <li>Account details (name, email, ceremony details).</li>
          <li>Survey responses (required where necessary for the service, or optional where you choose to provide them).</li>
          <li>Account preferences or other consent-based data.</li>
        </ul>
      </div>
      <div class="mt-6">
        <h3 class="font-semibold">Automatically Collected:</h3>
        <ul class="list-disc list-inside mt-2 space-y-1">
          <li>Usage data (IP address, browser type, device details, interaction logs).</li>
        </ul>
        <p class="mt-2">We do not collect precise location data. We do not sell advertising.</p>
      </div>
    </section>

    <!-- 3. How We Use Your Information -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">3. How We Use Your Information</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li>To deliver GraduationClips and related services.</li>
        <li>To distribute graduation videos and notifications on behalf of your university.</li>
        <li>To process payments (via Stripe).</li>
        <li>To manage student participation in graduation events.</li>
        <li>To provide aggregated analytics to universities.</li>
        <li>To analyse service usage and improve performance.</li>
        <li>To ensure platform security and stability.</li>
        <li>To comply with legal, regulatory, or contractual obligations.</li>
      </ul>
    </section>

    <!-- 4. Legal Basis for Processing -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">4. Legal Basis for Processing</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li><span class="font-semibold">Contractual necessity</span> – to provide the GraduationClip service you sign up for, including required survey questions where needed for service delivery.</li>
        <li><span class="font-semibold">Legitimate interests</span> – to ensure security, prevent fraud, analyse performance, and improve our platform (balanced against your rights).</li>
        <li><span class="font-semibold">Consent</span> – for optional survey responses or additional preferences not strictly required.</li>
        <li><span class="font-semibold">Legal obligation</span> – to comply with financial, tax, or regulatory requirements.</li>
        <li><span class="font-semibold">On behalf of universities (processor role)</span> – when processing student data supplied directly by your university (names, IDs, degree details, mandatory survey data, or ceremony footage), we act strictly under their instructions as set out in our Data Processing Agreement.</li>
      </ul>
    </section>

    <!-- 5. Sharing of Data -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">5. Sharing of Data</h2>
      <p class="mt-3">We may share your data with:</p>
      <ul class="list-disc list-inside mt-2 space-y-1">
        <li><span class="font-semibold">Your University</span> – for graduation management, required surveys, analytics, and service reporting.</li>
        <li><span class="font-semibold">Service Providers:</span>
          <ul class="list-disc list-inside ml-6 mt-1 space-y-1">
            <li>Stripe (payment processing).</li>
            <li>Amazon Web Services (AWS) (hosting, storage, encryption, serverless functions).</li>
          </ul>
        </li>
      </ul>
      <p class="mt-2">All providers operate under strict data protection agreements.</p>
    </section>

    <!-- 6. How We Store and Protect Your Data -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">6. How We Store and Protect Your Data</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li>Data is hosted on AWS.</li>
        <li>All data is encrypted in transit (TLS) and at rest (AES-256).</li>
        <li>Daily encrypted backups and vulnerability scans.</li>
        <li>Regular penetration tests and security reviews.</li>
        <li>ISO 27001 suppliers, PCI compliance, anomaly detection, firewalls, and access controls.</li>
      </ul>
    </section>

    <!-- 7. International Data Transfers -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">7. International Data Transfers</h2>
      <p class="mt-3">If your data is transferred outside the UK or EEA, we use safeguards such as Standard Contractual Clauses (SCCs) or adequacy decisions.</p>
    </section>

    <!-- 8. Data Retention -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">8. Data Retention</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li><span class="font-semibold">Account data:</span> retained while your account is active.</li>
        <li><span class="font-semibold">University-provided data:</span> retained only for the period agreed in the university contract (normally 5 years).</li>
        <li><span class="font-semibold">Directly provided data (including surveys):</span> we keep your data for 5 years unless specified otherwise in a contract with your university, or until you withdraw consent (for optional data).</li>
        <li><span class="font-semibold">Payment data:</span> retained as required by financial and tax laws.</li>
      </ul>
    </section>

    <!-- 9. Your Rights -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">9. Your Rights</h2>
      <p class="mt-3">Under GDPR, you can:</p>
      <ul class="list-disc list-inside mt-2 space-y-1">
        <li>Access a copy of your personal data.</li>
        <li>Correct inaccurate information.</li>
        <li>Request deletion of your data ("Right to be Forgotten"), subject to legal or contractual obligations.</li>
        <li>Restrict or object to processing.</li>
        <li>Request data portability.</li>
        <li>Withdraw consent at any time (where consent is the basis).</li>
        <li>Lodge a complaint with a supervisory authority.</li>
      </ul>
      <p class="mt-2">To exercise these rights, contact us at <a v-if="contactEmail" :href="`mailto:${contactEmail}`" class="text-blue-700 underline">{{ contactEmail }}</a><span v-else>[Insert contact email]</span>.</p>
    </section>

    <!-- 10. Cookies and Tracking -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">10. Cookies and Tracking</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li><span class="font-semibold">Essential cookies</span> – required for login and security.</li>
        <li><span class="font-semibold">Analytics and diagnostic data</span> – handled through our internal tooling to understand service usage and improve performance. We do not use Google Analytics.</li>
      </ul>
    </section>

    <!-- 11. Children’s Privacy -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">11. Children’s Privacy</h2>
      <p class="mt-3">Our services are intended only for university students. We do not knowingly collect data from anyone under 16.</p>
    </section>

    <!-- 12. Changes to This Policy -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">12. Changes to This Policy</h2>
      <p class="mt-3">We may update this Privacy Policy from time to time. The latest version will always be available on this page, with the month and year of revision shown at the top.</p>
    </section>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
  props: {
    lastUpdated: { type: String, default: "March 2026" },
    contactEmail: { type: String, default: "privacy@mygraduationclip.com" },
    postalAddress: { type: String, default: "" }
  },
  computed: {
    defaultUpdated() {
      // Fallback to a sensible default (e.g., current month and year)
      const date = new Date();
      const month = date.toLocaleString(undefined, { month: "long" });
      const year = date.getFullYear();
      return `${month} ${year}`;
    }
  }
};
</script>

<style scoped>
/* Optional: improve readability */
section + section { border-top: 1px solid #e5e7eb; padding-top: 2rem; }
</style>
